// filepath: apps/web/src/operator/OperatorSummaryRoute.tsx
//
// FR-UI-OPS-1 AC-3 / FR-UI-OPS-3 — the /operator/summary route.
//
// Composition:
//   - <SummaryCard>             — three headline metrics (Card grid)
//   - <SummaryTable>            — per-tenant breakdown (Card + Badge)
//   - 4-state error contract    — loading / success /
//                                 fetch-failure / 401-unauthorized
//                                 (+ loading skeleton via design-system
//                                 SkeletonLayout)
//
// 401 handling: when the server returns 401, the hook surfaces
// the error. The route calls `useOperatorToken().clear()` to
// return the user to the token prompt (the gate re-renders).
// Other failures render a generic fetch-failure surface with a
// retry button — the server's error envelope is never echoed.
//
// FR-UI-OPS-3 redesign: chrome uses design-system primitives
// (Banner, Button, Card) and a loading skeleton. The visible
// test ids (`operator-summary-route`, `operator-summary-fetch-error`,
// `operator-summary-retry`) and the 4-state status contract are
// PRESERVED verbatim. New: `operator-summary-loading` test id on
// the loading skeleton branch.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { Banner, Button, Card, SkeletonLayout } from "@/design-system";
import { SummaryCard } from "./SummaryCard";
import { SummaryTable } from "./SummaryTable";
import { TenantDrillDown } from "./TenantDrillDown";
import {
  useOperatorSummary,
  type OperatorSummaryStatus,
  type SummaryTenantRow,
} from "./useOperatorSummary";
import { useOperatorToken } from "./useOperatorToken";

/**
 * OperatorSummaryRoute — page body for `/operator/summary`.
 * Renders the four-state error contract plus the success card.
 */
export function OperatorSummaryRoute(): React.ReactElement {
  const { t } = useTranslation();
  const { data, status, refetch, unauthorized } = useOperatorSummary();
  const { clear } = useOperatorToken();
  // FR-UI-OPS-2 AC-3 — selected tenant drives the drill-down dialog.
  // Opening / closing is a pure in-SPA state transition (no reload).
  const [selectedTenant, setSelectedTenant] =
    React.useState<SummaryTenantRow | null>(null);

  // On 401, clear the token so the gate re-prompts. The effect
  // runs once per (status === "401-unauthorized") transition.
  React.useEffect(() => {
    if (unauthorized) {
      clear();
    }
  }, [unauthorized, clear]);

  return (
    <section
      data-testid="operator-summary-route"
      className="operator-summary-route"
      aria-labelledby="operator-summary-route-title"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-6)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <h1
        id="operator-summary-route-title"
        className="operator-summary-route-title"
        style={{
          fontSize: "var(--text-2xl)",
          fontWeight: "var(--font-extrabold)",
          color: "var(--text-primary)",
          margin: 0,
        }}
      >
        {t("operator.summary.title", "Cross-tenant summary")}
      </h1>
      <ErrorState status={status} onRetry={() => void refetch()} />
      {status === "loading" ? (
        <Card padding="lg" data-testid="operator-summary-loading">
          <SkeletonLayout />
        </Card>
      ) : null}
      {status === "success" && data ? (
        <>
          <SummaryCard summary={data} />
          <SummaryTable
            tenants={data.tenants}
            onRowClick={(row) => setSelectedTenant(row)}
          />
        </>
      ) : null}
      {selectedTenant ? (
        <TenantDrillDown
          row={selectedTenant}
          onClose={() => setSelectedTenant(null)}
        />
      ) : null}
    </section>
  );
}

interface ErrorStateProps {
  status: OperatorSummaryStatus;
  onRetry: () => void;
}

function ErrorState({
  status,
  onRetry,
}: ErrorStateProps): React.ReactElement | null {
  const { t } = useTranslation();
  if (status === "loading" || status === "success") return null;
  if (status === "401-unauthorized") {
    // The clear() in the parent effect will re-render the gate
    // with the prompt; render NOTHING here so the user does not
    // see a flash before the prompt appears.
    return null;
  }
  // status === "fetch-failure"
  return (
    <div
      data-testid="operator-summary-fetch-error"
      role="alert"
      className="operator-summary-fetch-error"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-4)",
      }}
    >
      <Banner tone="danger">
        {t("operator.error.fetchFailure", "Failed to load summary.")}
      </Banner>
      <div>
        <Button
          variant="primary"
          size="md"
          onClick={onRetry}
          data-testid="operator-summary-retry"
        >
          {t("operator.error.retry", "Retry")}
        </Button>
      </div>
    </div>
  );
}
